// Single source of truth for the workspace's features.
//
// The sidebar, the router, the Coming Soon panels, and the status bar all
// read from this list. Exactly one feature is `live` today (Frame Extractor);
// the rest render the in-development panel until they're built.

const FEATURES = [
  {
    id: 'frames',
    route: '/',
    n: '01',
    name: 'Frame Extractor',
    kind: 'Video → stills',
    headline: 'Pull stills from any video, locally.',
    live: true,
  },
  {
    id: 'sharpness',
    route: '/sharpness-scorer',
    n: '02',
    name: 'Sharpness Scorer',
    kind: 'Quality / ranking',
    headline: 'Find the sharpest frame in a stack.',
    about:
      'Rank every extracted frame by Laplacian variance so you can keep the crispest one. Useful after Frame Extractor — sweep an interval, then auto-select the keepers.',
    bullets: [
      'Laplacian-variance focus score per frame',
      'Side-by-side ranked browser with thresholds',
      'One-click filter to the top-N sharpest stills',
    ],
    eta: 'Q3 2026',
  },
  {
    id: 'segment',
    route: '/color-segmentation',
    n: '03',
    name: 'Color Segmentation',
    kind: 'HSV masks',
    headline: 'Threshold an image into vegetation, water, and built area.',
    about:
      'Fast HSV-space thresholding tuned for aerial imagery. Quick way to estimate land cover or pre-mask scenes before heavier models run.',
    bullets: [
      'Interactive HSV range sliders with live preview',
      'Vegetation / water / built / soil presets',
      'Export masks as PNG or as a region report',
    ],
    eta: 'Q3 2026',
  },
  {
    id: 'detect',
    route: '/object-detection',
    n: '04',
    name: 'Object Detection',
    kind: 'YOLOv8 · ONNX',
    headline: 'Run a real detector in the browser.',
    about:
      'YOLOv8 served via ONNX Runtime Web. WebGPU when available, WASM elsewhere. Runs entirely on your machine — the model downloads once and stays cached.',
    bullets: [
      '80-class COCO weights out of the box',
      'Confidence and IoU thresholds, live',
      'Export annotated frames or a JSON of detections',
    ],
    eta: 'Q4 2026',
  },
  {
    id: 'traffic',
    route: '/traffic-analysis',
    n: '05',
    name: 'Traffic Analysis',
    kind: 'Tracking · heatmaps',
    headline: 'Count and track vehicles, surface flow as a heatmap.',
    about:
      'Stack a tracker on top of detection to associate IDs over time. Output: counts by class, dwell time, and a density heatmap composited over the source frame.',
    bullets: [
      'ByteTrack-style association across frames',
      'Tripwire counters with editable polygons',
      'Heatmap overlay + CSV time series',
    ],
    eta: 'Q4 2026',
  },
  {
    id: 'mapper',
    route: '/aerial-mapper',
    n: '06',
    name: 'Aerial Mapper',
    kind: 'Stitching · orthomosaic',
    headline: 'Stitch a folder of aerial photos into one map.',
    about:
      'Feature-match overlapping photos and warp them into a single orthographic tile. Designed for the kind of thing a small drone flight produces.',
    bullets: [
      'ORB feature matching + RANSAC homography',
      'Progressive tile preview as the stitch advances',
      'Export GeoTIFF when EXIF GPS is present',
    ],
    eta: '2027',
  },
];

export const FEATURE_BY_ROUTE = Object.fromEntries(
  FEATURES.map((f) => [f.route, f])
);

export default FEATURES;
